/**
 * Legacy WordPress /blog/<slug> URLs.
 *
 * Values starting with "/" are absolute site paths; everything else resolves
 * to /resources/<value>. These 301s keep historical link equity flowing to the
 * current canonical destinations now that /blog serves live content again.
 */
export const LEGACY_BLOG_REDIRECTS: Record<string, string> = {
  "shopify-migration": "shopify-migration-timeline",
  "migrate-to-shopify": "shopify-migration-guide-2026",
  "shopify-migration-guide": "shopify-migration-guide-2026",
  "woocommerce-to-shopify": "woocommerce-to-shopify-migration",
  "magento-to-shopify": "magento-to-shopify-migration-what-agencies-get-wrong",
  "bigcommerce-to-shopify": "bigcommerce-to-shopify-migration-guide",
  "square-to-shopify": "shopify-migration-guide-2026",
  "5-custom-shopify-solutions": "custom-shopify-solutions-for-established-brands",
  // NOTE: /blog/custom-shopify-development is now a live post, not a legacy slug.
  "shopify-store-setup-experts": "shopify-store-setup-playbook",
  "shopify-store-setup": "shopify-store-setup-playbook",
  "how-to-set-up-a-shopify-store": "shopify-store-setup-playbook",
  "ecommerce-seo-agency": "shopify-technical-seo-playbook",
  "shopify-seo": "shopify-technical-seo-playbook",
  "shopify-seo-guide": "shopify-technical-seo-playbook",
  "technical-seo-shopify": "shopify-technical-seo-playbook",
  "how-to-add-a-size-guide-on-shopify": "shopify-size-guide-implementation",
  "shopify-size-guide": "shopify-size-guide-implementation",
  "size-guide-shopify": "shopify-size-guide-implementation",
  "create-a-personalized-product-recommender": "shopify-liquid-product-recommender",
  "shopify-product-recommendations": "shopify-liquid-product-recommender",
  "product-recommender-shopify": "shopify-liquid-product-recommender",
  "shopify-software-development": "shopify-development-guide",
  "shopify-development": "shopify-development-guide",
  "shopify-app-development": "shopify-development-guide",
  "common-shopify-issues-and-solutions": "common-shopify-issues-fixes",
  "common-shopify-problems": "common-shopify-issues-fixes",
  "shopify-issues": "common-shopify-issues-fixes",
  "how-to-become-shopify-developer": "become-a-shopify-developer",
  "shopify-developer-career": "become-a-shopify-developer",
  "become-shopify-developer": "become-a-shopify-developer",
  "ai-vs-professional-web-developers-for-e-commerce-business-websites-what-works-best-in-2025":
    "ai-vs-professional-web-developers-2026",
  "ai-vs-developers": "ai-vs-professional-web-developers-2026",
  "ai-web-development": "ai-vs-professional-web-developers-2026",
  "shopify-collabs": "how-to-use-shopify-collabs",
  "how-to-use-shopify-collabs": "how-to-use-shopify-collabs",
  "shopify-scripts-to-functions": "shopify-scripts-deprecation-migration-2026",
  "shopify-scripts-deprecation": "shopify-scripts-deprecation-migration-2026",
  "shopify-functions-migration": "shopify-functions",
  "shopify-experts": "/shopify-experts",
  "shopify-plus-experts": "/shopify-experts",
  "shugert-marketings-ceo-reflects-on-what-sets-them-apart": "/about",
  "three-pain-points-of": "common-shopify-issues-fixes",
  "what-do-data-scientists-do-exactly": "become-a-shopify-developer",
  "machine-learning-for-small-business-practical-ways-to-use-it-today": "shopify-development-guide",
  // Ahrefs crawl 2026-09-01: legacy WordPress posts that were still 404ing.
  "shugert-marketing-wins-2021-clutch-award-for-mexicos-top-big-data-analytics-company": "/about",
  "integrating-ai-into-your-shopify-store": "shopify-agentic-storefronts-may-2026",
  "how-to-avoid-a-bad-data-drive-decision-that-will-make-you-broke": "/analytics-ai",
  "big-data-marketing": "/analytics-ai",
  "data-science": "/analytics-ai",
  "5-concepts-you-want-to-know-related-to-data-science": "/analytics-ai",
  // Outrank legacy URLs: consolidate into current canonical articles while preserving required Exchange links.
  "shopify-plus-support": "/blog/shopify-plus-vs-bigcommerce",
  "shopify-hydrogen": "/blog/shopify-headless-commerce",
  "shopify-cart-abandonment": "/blog/shopify-plus-cro-experts",
  "shopify-conversion-rate-optimization": "/blog/shopify-plus-cro-experts",
  // Outrank slug standardization 2026-09-02: shortened to the focus keyword.
  "when-to-use-custom-shopify-development": "/blog/custom-shopify-development",
  "shopify-plus-vs-bigcommerce-enterprise-brands": "/blog/shopify-plus-vs-bigcommerce",
  // Outrank batch 2026-09-07: renamed to prevent cannibalization / clarify intent.
  "technical-seo-for-ecommerce": "/blog/ecommerce-technical-seo-engineering",
  "magento-to-shopify-migration": "/blog/before-magento-to-shopify-migration",
  "conversion-optimization-experts-guide-for-shopify-plus": "/blog/shopify-plus-cro-experts",
  // Outrank 2026-09-25: original partner-intent slug was repurposed after editorial review.
  "shopify-plus-partner": "/blog/shopify-plus-technical-debt",
  "shopify-plus-partners": "/blog/shopify-plus-technical-debt",
  // Outrank 2026-10-08: preserve original Exchange checker URL for the canonical product SEO guide.
  "shopify-product-page-seo": "/blog/shopify-product-page-seo-architecture",
};

/**
 * Legacy WordPress taxonomy slugs under /blog/category/<slug> (and their deep
 * feed/child variants) that no longer exist as Insights categories. These map
 * straight to the current canonical destination so Googlebot gets a single
 * 301 instead of a redirect into a 404.
 */
export const LEGACY_BLOG_CATEGORY_REDIRECTS: Record<string, string> = {
  "data-science": "/analytics-ai",
};

/** Resolves a legacy blog slug to an absolute site path, or null. */
export function resolveLegacyBlogPath(slug: string): string | null {
  const target = LEGACY_BLOG_REDIRECTS[slug];
  if (!target) return null;
  return target.startsWith("/") ? target : `/resources/${target}`;
}

/** Resolves a legacy blog category slug to an absolute site path, or null. */
export function resolveLegacyBlogCategoryPath(category: string): string | null {
  return LEGACY_BLOG_CATEGORY_REDIRECTS[category] ?? null;
}
